import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import DraftsList from './DraftsList';
import MediaThumb from './MediaThumb';
import StatusPill from './StatusPill';
import ThreadPanel from './ThreadPanel';
import useCreativeThread from '../hooks/useCreativeThread';
import {
  canMessage,
  canPick,
  canSubmitVariations,
  formatEpoch,
  isVariationFilled,
  isVideoSubType,
  subTypeLabel,
} from '../config';

/**
 * One segmented control, four views. Module level so the array identity is
 * stable and can never leak into a dependency list.
 */
const VIEWS = [
  { key: 'info', label: 'Info', icon: 'info' },
  { key: 'activity', label: 'Activity', icon: 'messages-square' },
  { key: 'drafts', label: 'Drafts', icon: 'file-text' },
  { key: 'media', label: 'Media', icon: 'image' },
];

const DEFAULT_VIEW = 'activity';

const MetaRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-medium text-slate-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm text-slate-800">{value || '—'}</span>
  </div>
);

/**
 * The creative's CURRENT live media — read-only reference. A content writer
 * never uploads media or edits these fields directly; they write
 * `variations` instead (see the Drafts tab). There is no upload affordance
 * here, unlike the designer panel's Media tab.
 */
const MediaPreview = ({ row }) => {
  const [imgBroken, setImgBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const video = isVideoSubType(row?.sub_type);

  useEffect(() => {
    setImgBroken(false);
    setVideoBroken(false);
  }, [row?.img_urls, row?.video_urls]);

  const hasVideo = video && !!row?.video_urls && !videoBroken;
  const hasImage = !!row?.img_urls && !imgBroken;
  const stored = !!(row?.img_urls || row?.video_urls);

  if (!hasVideo && !hasImage) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
        <SlIcon
          library="lucide"
          name={video ? 'video-off' : 'image-off'}
          className="mb-2 text-slate-300"
          style={{ fontSize: '24px' }}
        />
        <p className="text-sm font-medium text-slate-600">
          {stored ? 'Preview unavailable' : 'Nothing uploaded yet'}
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {stored
            ? 'The stored file could not be loaded.'
            : 'The designer has not uploaded a design for this creative yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {hasVideo ? (
        <video
          src={row.video_urls}
          poster={hasImage ? row.img_urls : undefined}
          controls
          onError={() => setVideoBroken(true)}
          className="max-h-72 w-full rounded-xl border border-slate-200 bg-black"
        />
      ) : (
        <img
          src={row.img_urls}
          alt={row.creative_name || 'creative'}
          onError={() => setImgBroken(true)}
          className="max-h-72 w-full rounded-xl border border-slate-200 bg-slate-50 object-contain"
        />
      )}
      {hasVideo && hasImage && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2">
          <MediaThumb src={row.img_urls} alt="Thumbnail" className="h-12 w-12" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700">Thumbnail</p>
            <p className="text-[11px] text-slate-400">Shown before the video plays</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Right-side detail drawer (full screen below `sm`).
 *
 * Owns the thread hook for the selected creative; every action is delegated
 * up to `ContentWriterHome` so the list, the counts and the thread all update
 * from one response — no refetch loop, no polling. Same shape as the
 * designer panel's `DetailDrawer`, minus the Versions segment: there is no
 * `/versions` endpoint here, so "Drafts" reads straight off
 * `detail.variations` instead.
 */
const DetailDrawer = ({
  open,
  row,
  onClose,
  onPick,
  onOpenSubmit,
  onSendMessage,
  onSendAttachment,
  incoming,
  pickBusy,
}) => {
  const bscId = row?.bsc_id ?? null;
  const [view, setView] = useState(DEFAULT_VIEW);
  const [zoom, setZoom] = useState(null);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const appliedNonceRef = useRef(null);

  const {
    detail,
    detailLoading,
    detailError,
    reloadDetail,
    logs,
    logsLoading,
    logsError,
    reloadLogs,
    applyIncoming,
    applyDetailPatch,
  } = useCreativeThread(bscId, { enabled: open, seedRow: row });

  const current = detail || row;

  useEffect(() => {
    if (!open) return;
    setView(DEFAULT_VIEW);
    setZoom(null);
    appliedNonceRef.current = null;
  }, [open, bscId]);

  // The single seam for externally produced activity (action responses today,
  // a socket subscription later). Keyed on a primitive nonce.
  useEffect(() => {
    if (!incoming || !bscId) return;
    if (Number(incoming.bsc_id) !== Number(bscId)) return;
    if (appliedNonceRef.current === incoming.nonce) return;
    appliedNonceRef.current = incoming.nonce;
    if (incoming.logs?.length) applyIncoming(incoming.logs);
    if (incoming.patch) applyDetailPatch(incoming.patch);
  }, [incoming, bscId, applyIncoming, applyDetailPatch]);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const body = document.body;
    const outlet = document.querySelector('.outlet-body');
    const prevBody = body.style.overflow;
    const prevOutlet = outlet ? outlet.style.overflow : '';
    body.style.overflow = 'hidden';
    if (outlet) outlet.style.overflow = 'hidden';
    const timer = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      body.style.overflow = prevBody;
      if (outlet) outlet.style.overflow = prevOutlet;
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  const closeZoom = useCallback(() => setZoom(null), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (zoom) {
        e.stopPropagation();
        closeZoom();
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, zoom, closeZoom, onClose]);

  const handleZoom = useCallback((url, isVideo) => setZoom({ url, isVideo }), []);

  const handleSend = useCallback(
    async (text) => onSendMessage(bscId, text),
    [onSendMessage, bscId]
  );

  const handleAttach = useCallback(
    (file, caption, onUploadProgress) => onSendAttachment(bscId, file, caption, onUploadProgress),
    [onSendAttachment, bscId]
  );

  const messageCount = useMemo(
    () => logs.reduce((n, log) => (log?.activity_type === 'message' ? n + 1 : n), 0),
    [logs]
  );

  if (!open || !current) return null;

  const status = Number(current.status);
  const showPick = canPick(status);
  const showSubmit = canSubmitVariations(status);
  const hasMedia = !!(current.img_urls || current.video_urls);
  const draftCount = Array.isArray(current.variations)
    ? current.variations.filter(isVariationFilled).length
    : 0;

  const badgeFor = (key) => {
    if (key === 'activity') return messageCount > 0 ? String(messageCount) : null;
    if (key === 'drafts') return draftCount > 0 ? String(draftCount) : null;
    return null;
  };

  const submitButton = (
    <button
      type="button"
      onClick={() => onOpenSubmit(current)}
      className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
    >
      <SlIcon library="lucide" name="file-text" style={{ fontSize: '14px' }} />
      Submit drafts
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Creative ${current.creative_name || current.bsc_id}`}
        tabIndex={-1}
        className="flex h-full w-full flex-col bg-white shadow-2xl outline-none sm:max-w-xl"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold text-slate-900">
                {current.creative_name || `Creative #${current.bsc_id}`}
              </h2>
              <StatusPill status={current.status} />
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              #{current.bsc_id} · {subTypeLabel(current.sub_type)}
              {detailLoading ? ' · refreshing…' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '18px' }} />
          </button>
        </header>

        {detailError && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            <span>{detailError}</span>
            <button
              type="button"
              onClick={reloadDetail}
              className="rounded border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-800"
            >
              Retry
            </button>
          </div>
        )}

        <div
          role="tablist"
          aria-label="Creative views"
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-2 py-2"
        >
          {VIEWS.map((v) => {
            const active = view === v.key;
            const badge = badgeFor(v.key);
            return (
              <button
                key={v.key}
                type="button"
                role="tab"
                id={`cw-drawer-tab-${v.key}`}
                aria-selected={active}
                aria-controls={`cw-drawer-panel-${v.key}`}
                onClick={() => setView(v.key)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                  active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <SlIcon library="lucide" name={v.icon} style={{ fontSize: '14px' }} />
                {v.label}
                {badge && (
                  <span
                    className={`rounded-full px-1.5 text-[10px] font-semibold ${
                      active ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {badge}
                  </span>
                )}
                {v.key === 'media' && hasMedia && (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                    title="Media is attached"
                  />
                )}
              </button>
            );
          })}
        </div>

        {(showPick || showSubmit) && (
          <div className="flex shrink-0 flex-wrap gap-2 border-b border-slate-200 px-4 py-2.5">
            {showPick && (
              <button
                type="button"
                disabled={pickBusy}
                onClick={() => onPick(current)}
                className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pickBusy ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-300 border-t-white" />
                ) : (
                  <SlIcon library="lucide" name="hand" style={{ fontSize: '14px' }} />
                )}
                {pickBusy ? 'Picking…' : 'Pick this creative'}
              </button>
            )}
            {showSubmit && submitButton}
          </div>
        )}

        <div
          role="tabpanel"
          id={`cw-drawer-panel-${view}`}
          aria-labelledby={`cw-drawer-tab-${view}`}
          className="min-h-0 flex-1"
        >
          {view === 'info' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 px-3">
                <MetaRow
                  label="Sales person"
                  value={
                    current.sales_person_name
                      ? `${current.sales_person_name}${current.sales_person_id ? ` (#${current.sales_person_id})` : ''}`
                      : current.sales_person_id
                        ? `#${current.sales_person_id}`
                        : '—'
                  }
                />
                <MetaRow
                  label="Assigned by"
                  value={
                    current.assigned_by_name ||
                    (current.assigned_by_id ? `#${current.assigned_by_id}` : '—')
                  }
                />
                <MetaRow label="Assigned" value={formatEpoch(current.created_time)} />
                <MetaRow label="Creative type" value={current.creative_type} />
                <div className="flex items-center justify-between gap-4 py-2">
                  <span className="shrink-0 text-xs font-medium text-slate-500">Current status</span>
                  <StatusPill status={current.status} size="md" />
                </div>
              </div>

              {current.instructions && (
                <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Instructions
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
                    {current.instructions}
                  </p>
                </div>
              )}

              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Current live content (read-only)
                </p>
                <div className="divide-y divide-slate-200">
                  <MetaRow label="Headline" value={current.head_line} />
                  <MetaRow label="Description" value={current.description} />
                  <MetaRow label="Link text" value={current.text_in_link} />
                </div>
              </div>
            </div>
          )}

          {view === 'activity' && (
            <ThreadPanel
              logs={logs}
              loading={logsLoading}
              error={logsError}
              onRetry={reloadLogs}
              canSend={canMessage(status)}
              canSubmitDrafts={showSubmit}
              lockedReason="This conversation is closed."
              onSend={handleSend}
              onSendAttachment={handleAttach}
              onOpenSubmit={() => onOpenSubmit(current)}
              onZoom={handleZoom}
            />
          )}

          {view === 'drafts' && (
            <div className="h-full overflow-y-auto bg-slate-50">
              <DraftsList
                variations={current.variations}
                canSubmitDrafts={showSubmit}
                onOpenSubmit={() => onOpenSubmit(current)}
              />
            </div>
          )}

          {view === 'media' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <MediaPreview row={current} />
              <p className="flex items-start gap-1.5 text-xs text-slate-500">
                <SlIcon
                  library="lucide"
                  name="info"
                  className="mt-[1px] shrink-0"
                  style={{ fontSize: '13px' }}
                />
                <span>
                  This is the designer&apos;s current live media, shown for reference only. You
                  write headline, description and link text in Drafts — you cannot edit media
                  here.
                </span>
              </p>
            </div>
          )}
        </div>
      </aside>

      {zoom && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeZoom();
          }}
        >
          <button
            type="button"
            onClick={closeZoom}
            aria-label="Close preview"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '20px' }} />
          </button>
          {zoom.isVideo ? (
            <video src={zoom.url} controls autoPlay className="max-h-[88vh] max-w-full rounded-lg" />
          ) : (
            <img
              src={zoom.url}
              alt="preview"
              className="max-h-[88vh] max-w-full rounded-lg object-contain"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DetailDrawer;
